import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Descriptions,
  Input,
  Message,
  Modal,
  Space,
  Tag,
  Typography,
} from '@arco-design/web-react';
import { IconEye, IconLeft } from '@arco-design/web-react/icon';
import { customersApi } from '@/api';
import { formatMoney } from '@/utils/money';
import type { CustomerDetail, CustomerSummary } from '@/types/customer';
import WalletTransferModal from './WalletTransferModal';
import ResetPasswordModal from './ResetPasswordModal';

type RevealField = 'email' | 'phone';

export default function CustomerDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState<Partial<Record<RevealField, string>>>({});
  const [transferOpen, setTransferOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    customersApi
      .detail(id)
      .then(setDetail)
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const revealField = (field: RevealField) => {
    if (!detail) return;
    let reason = '';
    Modal.confirm({
      title: `查看「${field === 'email' ? '邮箱' : '手机'}」明文`,
      content: (
        <div>
          <div style={{ marginBottom: 8, color: 'var(--admin-text-secondary)' }}>
            该操作会写入审计日志（操作人、时间、IP、原因）。请填写查看原因：
          </div>
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 4 }}
            placeholder="如：客户工单 #12345 投诉处理"
            onChange={(v) => (reason = v)}
          />
        </div>
      ),
      okText: '提交并查看',
      cancelText: '取消',
      onOk: async () => {
        if (!reason.trim() || reason.trim().length < 4) {
          Message.warning('请填写至少 4 个字符的查看原因');
          throw new Error('reason required');
        }
        const resp = await customersApi.viewSensitive({
          customerId: detail.id,
          fields: [field],
          reason: reason.trim(),
        });
        setRevealed((prev) => ({ ...prev, ...resp }));
        Message.success('已记录审计日志');
      },
    });
  };

  const asSummary = (d: CustomerDetail): CustomerSummary => ({
    id: d.id,
    username: d.username,
    emailMasked: d.emailMasked,
    phoneMasked: d.phoneMasked,
    walletBalanceCents: d.walletBalanceCents,
    enabled: d.enabled,
    cardCount: d.cardCount,
    createdAt: d.createdAt,
  });

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <Space>
          <Button icon={<IconLeft />} onClick={() => navigate(-1)}>
            返回
          </Button>
          <div>
            <div className="admin-page__title">客户详情</div>
            <div className="admin-page__subtitle">
              敏感信息默认脱敏；查看明文将留痕审计
            </div>
          </div>
        </Space>
        {detail && (
          <Space>
            <Button type="primary" onClick={() => setTransferOpen(true)}>
              划拨资金
            </Button>
            <Button onClick={() => setResetOpen(true)}>重置登录密码</Button>
          </Space>
        )}
      </div>

      <div className="admin-card">
        {loading || !detail ? (
          <Typography.Text type="secondary">加载中…</Typography.Text>
        ) : (
          <Descriptions
            data={[
              { label: '用户 ID', value: <span className="admin-mono">{detail.id}</span> },
              { label: '用户名', value: detail.username },
              {
                label: '状态',
                value: detail.enabled ? (
                  <Tag color="green">启用</Tag>
                ) : (
                  <Tag color="red">停用</Tag>
                ),
              },
              {
                label: '母账户余额',
                value: (
                  <strong style={{ fontSize: 16 }}>
                    {formatMoney(detail.walletBalanceCents)}
                  </strong>
                ),
              },
              {
                label: '邮箱',
                value: (
                  <Space>
                    <span className="admin-mono">
                      {revealed.email ?? detail.emailMasked}
                    </span>
                    {!revealed.email && (
                      <Button
                        size="mini"
                        type="text"
                        icon={<IconEye />}
                        onClick={() => revealField('email')}
                      >
                        查看明文
                      </Button>
                    )}
                  </Space>
                ),
              },
              {
                label: '手机',
                value: (
                  <Space>
                    <span className="admin-mono">
                      {revealed.phone ?? detail.phoneMasked ?? '-'}
                    </span>
                    {detail.phoneMasked && !revealed.phone && (
                      <Button
                        size="mini"
                        type="text"
                        icon={<IconEye />}
                        onClick={() => revealField('phone')}
                      >
                        查看明文
                      </Button>
                    )}
                  </Space>
                ),
              },
              { label: '持卡人模板数', value: detail.billingProfileCount },
              { label: '已开通卡片数', value: detail.cardCount },
              { label: '最近登录', value: detail.lastLoginAt ?? '-' },
              { label: '注册时间', value: detail.createdAt },
            ]}
            column={2}
            labelStyle={{ width: 140, color: 'var(--admin-text-tertiary)' }}
          />
        )}
      </div>

      {detail && (
        <>
          <WalletTransferModal
            customer={asSummary(detail)}
            visible={transferOpen}
            onClose={() => setTransferOpen(false)}
            onSuccess={load}
          />
          <ResetPasswordModal
            customer={asSummary(detail)}
            visible={resetOpen}
            onClose={() => setResetOpen(false)}
          />
        </>
      )}
    </div>
  );
}
